import type { EntityKind } from "@/components/entity-preview/entity-kinds";
import type { IconName } from "@/components/icons/Icon";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useCallback, useMemo } from "react";

export interface ProjectNavPin extends Record<string, unknown> {
  /** The same key a Recent entry carries, so a pinned page drops out of Recent. */
  key: string;
  name: string;
  href: string;
  icon: IconName;
  /** The object pinned, for its preview; absent for pages no entity kind recognizes. */
  entity?: { kind: EntityKind; id: string };
}

interface ProjectNavPinStore extends Record<string, unknown> {
  pinsByEnvironment: Record<string, ProjectNavPin[]>;
}

const EMPTY_PIN_STORE: ProjectNavPinStore = { pinsByEnvironment: {} };

/** Appends a pin, so pinned pages keep the order they were pinned in; pinning twice is a no-op. */
export const addProjectNavPin = (
  pins: readonly ProjectNavPin[],
  pin: ProjectNavPin,
): ProjectNavPin[] =>
  pins.some((existing) => existing.key === pin.key) ? [...pins] : [...pins, pin];

export const removeProjectNavPin = (pins: readonly ProjectNavPin[], key: string): ProjectNavPin[] =>
  pins.filter((pin) => pin.key !== key);

/** Pages pinned in this environment, with commands to pin and unpin. */
export const useProjectNavPins = () => {
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const [store, setStore] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_PINS,
    EMPTY_PIN_STORE,
  );
  const environmentKey =
    projectId == null || environmentId == null ? undefined : `${projectId}:${environmentId}`;

  const pins = useMemo(
    () => (environmentKey == null ? [] : store.pinsByEnvironment?.[environmentKey] ?? []),
    [environmentKey, store],
  );
  const updatePins = useCallback(
    (update: (pins: readonly ProjectNavPin[]) => ProjectNavPin[]) => {
      if (environmentKey == null) return;
      setStore((current) => {
        const pinsByEnvironment = current?.pinsByEnvironment ?? {};
        return {
          pinsByEnvironment: {
            ...pinsByEnvironment,
            [environmentKey]: update(pinsByEnvironment[environmentKey] ?? []),
          },
        };
      });
    },
    [environmentKey, setStore],
  );
  const pin = useCallback(
    (nextPin: ProjectNavPin) => updatePins((current) => addProjectNavPin(current, nextPin)),
    [updatePins],
  );
  const unpin = useCallback(
    (key: string) => updatePins((current) => removeProjectNavPin(current, key)),
    [updatePins],
  );

  return { pins, pin, unpin };
};
